// js/lib/cache.js
// sessionStorage-backed in-session cache with per-entry TTL.
//
// Prevents redundant Firestore reads when the user navigates between pages.
// All entries are scoped with a prefix so they can be bulk-cleared on sign-out.
//
// Usage:
//   cacheSet('shop:abc123', shopData, 10 * 60 * 1000);  // 10 min
//   const shop = cacheGet('shop:abc123');                 // null if expired
//   cacheClearAll();                                       // on sign-out

const PREFIX = "lapp_";
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Store a value under `key` with an optional TTL in milliseconds.
 * Silently fails if sessionStorage is unavailable (private mode, quota, etc.).
 */
export function cacheSet(key, value, ttlMs = DEFAULT_TTL) {
  try {
    sessionStorage.setItem(
      PREFIX + key,
      JSON.stringify({ v: value, exp: Date.now() + ttlMs }),
    );
  } catch (_) {
    // Storage full or unavailable — gracefully skip caching.
  }
}

/**
 * Retrieve a cached value by key.
 * Returns null if not found or expired (expired entries are removed).
 */
export function cacheGet(key) {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() > entry.exp) {
      sessionStorage.removeItem(PREFIX + key);
      return null;
    }
    return entry.v;
  } catch (_) {
    return null;
  }
}

/**
 * Remove a single cache entry by key.
 */
export function cacheDel(key) {
  try {
    sessionStorage.removeItem(PREFIX + key);
  } catch (_) {}
}

/**
 * Clear ALL cache entries for this app.
 * Must be called on user sign-out to prevent the next user in the same
 * browser session from seeing stale data.
 */
export function cacheClearAll() {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (_) {}
}
